import { useState } from "react";
import { format } from "date-fns";
import { X, Plus, Trash2 } from "lucide-react";
import { DateAvailability, TimeSlot } from "./types";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import Modal from "@/components/ui/Modal";

interface Props {
  data: DateAvailability;
  onClose: () => void;
  isOpen: boolean;
  onSave: (updated: DateAvailability) => void;
}

const generateId = () => crypto.randomUUID();

const timeToMinutes = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const hasConflict = (slots: TimeSlot[]) => {
  const sorted = [...slots].sort(
    (a, b) => timeToMinutes(a.start) - timeToMinutes(b.start),
  );

  for (let i = 0; i < sorted.length - 1; i++) {
    const currentEnd = timeToMinutes(sorted[i].end);
    const nextStart = timeToMinutes(sorted[i + 1].start);
    if (currentEnd > nextStart) return true;
  }
  return false;
};

const EditAvailabilityModal = ({ data, onClose, onSave, isOpen }: Props) => {
  const [isAllDay, setIsAllDay] = useState(data.isAllDay);
  const [slots, setSlots] = useState<TimeSlot[]>(data.timeSlots);
  const [error, setError] = useState<string | null>(null);

  const addSlot = () => {
    setSlots([...slots, { id: generateId(), start: "", end: "" }]);
  };

  const updateSlot = (id: string, key: "start" | "end", value: string) => {
    setSlots((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [key]: value } : s)),
    );
  };

  const removeSlot = (id: string) => {
    setSlots((prev) => prev.filter((s) => s.id !== id));
  };

  const validateAndSave = () => {
    if (!isAllDay) {
      for (const slot of slots) {
        if (!slot.start || !slot.end)
          return setError("All slots must have start and end time");

        if (timeToMinutes(slot.end) <= timeToMinutes(slot.start))
          return setError("End time must be after start time");
      }

      if (hasConflict(slots)) return setError("Time slots are overlapping");
    }

    setError(null);
    onSave({
      ...data,
      isAllDay,
      timeSlots: isAllDay ? [] : slots,
    });
  };

  return (
    <Modal isOpen={isOpen} setOpen={() => onClose()}>
      <div className="">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-card space-y-4"
        >
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-lg">
              {format(new Date(data.date), "EEE, do MMM")}
            </h2>
            <button onClick={onClose}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* All Day */}
          {/* <button
            onClick={() => setIsAllDay(!isAllDay)}
            className={cn(
              "w-full py-2 rounded-lg border text-sm",
              isAllDay
                ? "bg-success/10 border-success text-success"
                : "border-border",
            )}
          >
            Available All Day
          </button> */}

          {!isAllDay && (
            <>
              <div className="space-y-2">
                {slots.map((slot) => (
                  <div key={slot.id} className="flex gap-2 items-center">
                    <input
                      type="time"
                      value={slot.start}
                      onChange={(e) =>
                        updateSlot(slot.id, "start", e.target.value)
                      }
                      className="border rounded px-2 py-1 w-full"
                    />
                    <input
                      type="time"
                      value={slot.end}
                      onChange={(e) =>
                        updateSlot(slot.id, "end", e.target.value)
                      }
                      className="border rounded px-2 py-1 w-full"
                    />
                    <button onClick={() => removeSlot(slot.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={addSlot}
                className="flex items-center gap-2 text-sm text-primary"
              >
                <Plus className="w-4 h-4" />
                Add Slot
              </button>
            </>
          )}

          {error && <div className="text-sm text-destructive">{error}</div>}

          <button
            onClick={validateAndSave}
            className="w-full bg-primary text-primary-foreground py-2 rounded-lg"
          >
            Save Changes
          </button>
        </motion.div>
      </div>
    </Modal>
  );
};

export default EditAvailabilityModal;
